import { Box, Text, useColorModeValue } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { ClarificationQuestion } from '../../types/chatTypes'
import { FormFooter } from './FormFooter'
import { OptionList } from './OptionList'
import { QuestionHeader } from './QuestionHeader'
import {
  Selection,
  countSelected,
  formatQA,
  hasAnswer,
} from './selection'
import { useClarificationKeyboard } from './useClarificationKeyboard'

interface Props {
  questions: ClarificationQuestion[]
  onSubmit: (qaString: string) => void
}

export const ClarificationForm: React.FC<Props> = ({ questions, onSubmit }) => {
  const [selections, setSelections] = useState<Selection[]>(() =>
    questions.map(() => ({ kind: 'none' })),
  )
  const [currentIndex, setCurrentIndex] = useState(0)
  const [focusedRow, setFocusedRow] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useLanguage()

  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const mutedColor = useColorModeValue('gray.500', 'gray.400')

  const total = questions.length
  const isLast = currentIndex === total - 1
  const currentQuestion = questions[currentIndex]
  const currentSelection = selections[currentIndex]
  const escape = currentQuestion.options.find(o => o.is_escape_hatch)
  const choices = currentQuestion.options.filter(o => !o.is_escape_hatch)
  const totalRows = choices.length + (escape ? 1 : 0)
  const isMulti = currentQuestion.type === 'multi_select'
  const answered = hasAnswer(currentSelection)

  // Reset row focus and pull keyboard focus back to the form when the
  // displayed question changes (mount + step-through).
  useEffect(() => {
    setFocusedRow(0)
    containerRef.current?.focus()
  }, [currentIndex])

  const updateCurrent = (next: Selection) => {
    setSelections(prev => prev.map((s, i) => (i === currentIndex ? next : s)))
  }

  const advance = (overrideCurrent?: Selection) => {
    if (submitted) return
    const nextSelections = overrideCurrent
      ? selections.map((s, i) => (i === currentIndex ? overrideCurrent : s))
      : selections
    if (overrideCurrent) setSelections(nextSelections)
    if (isLast) {
      setSubmitted(true)
      onSubmit(formatQA(questions, nextSelections))
    } else {
      setCurrentIndex(i => i + 1)
    }
  }

  const handleSkip = () => advance({ kind: 'none' })
  const handleForward = () => {
    if (answered) advance()
  }

  const togglePick = (label: string) => {
    if (isMulti) {
      const current =
        currentSelection.kind === 'pick' ? currentSelection.values : []
      const next = current.includes(label)
        ? current.filter(v => v !== label)
        : [...current, label]
      updateCurrent(
        next.length === 0 ? { kind: 'none' } : { kind: 'pick', values: next },
      )
    } else {
      updateCurrent({ kind: 'pick', values: [label] })
    }
  }

  const activateFocusedRow = () => {
    if (focusedRow < choices.length) {
      togglePick(choices[focusedRow].label ?? '')
    } else if (escape && currentSelection.kind !== 'custom') {
      // EscapeHatchRow focuses its input when this transitions to active.
      updateCurrent({ kind: 'custom', text: '' })
    }
  }

  useClarificationKeyboard({
    containerRef,
    enabled: !submitted,
    totalRows,
    hasAnswer: answered,
    onSkip: handleSkip,
    onSubmit: handleForward,
    onActivate: activateFocusedRow,
    onMoveFocus: setFocusedRow,
  })

  const selectedCount = countSelected(currentSelection)
  const counterText =
    isMulti || selectedCount > 0 ? t('clarification.selectedCount', { count: selectedCount }) : ''

  return (
    <Box>
      <Box
        ref={containerRef}
        tabIndex={-1}
        bg={cardBg}
        border='1px solid'
        borderColor={borderColor}
        borderRadius='lg'
        boxShadow='lg'
        overflow='hidden'
        outline='none'
        _focus={{ outline: 'none', boxShadow: 'lg' }}
      >
        <QuestionHeader
          prompt={currentQuestion.prompt}
          isMulti={isMulti}
          currentIndex={currentIndex}
          total={total}
          disabled={submitted}
          onPrev={() => setCurrentIndex(i => Math.max(0, i - 1))}
          onNext={() => setCurrentIndex(i => Math.min(total - 1, i + 1))}
        />
        <OptionList
          choices={choices}
          escape={escape}
          isMulti={isMulti}
          selection={currentSelection}
          focusedRow={focusedRow}
          disabled={submitted}
          onTogglePick={togglePick}
          onSetSelection={updateCurrent}
        />
        <FormFooter
          counterText={counterText}
          isLast={isLast}
          canSubmit={answered}
          disabled={submitted}
          onSkip={handleSkip}
          onForward={handleForward}
        />
      </Box>
      <Text fontSize='xs' color={mutedColor} mt={2} textAlign='center'>
        {t('clarification.shortcutHint')}
      </Text>
    </Box>
  )
}
